import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Briefcase, ChevronLeft } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { marketplaceService } from '../services/marketplaceService';
import { Loading } from '../components/UI/Loading';

export const MarketplacePage = () => {
  const [selectedCity, setSelectedCity] = useState('');
  
  // This would fetch installers from API
  const { data: installers, isLoading } = useQuery({
    queryKey: ['installers'],
    queryFn: () => marketplaceService.getInstallers?.() || Promise.resolve([]),
  });

  const cities = ['تهران', 'اصفهان', 'شیراز', 'مشهد', 'تبریز'];

  // Mock data for demonstration
  const mockInstallers = [
    {
      id: 1,
      company_name: 'نصب سولار ایران',
      user: { username: 'solar_tehran' },
      experience_years: 8,
      rating: 4.8,
      total_reviews: 45,
      service_areas: ['تهران', 'کرج'],
      bio: 'تخصص در نصب سیستم‌های خانگی و صنعتی',
    },
    {
      id: 2,
      company_name: 'انرژی پاک',
      user: { username: 'clean_energy' },
      experience_years: 5,
      rating: 4.5,
      total_reviews: 28,
      service_areas: ['اصفهان', 'شیراز'],
      bio: 'ارائه خدمات مشاوره و نصب',
    },
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            بازارچه نصاب‌های حرفه‌ای
          </h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            بهترین نصاب‌های خورشیدی را پیدا کنید و با خیال راحت پروژه خود را به آن‌ها بسپارید
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-8">
          <div className="flex flex-wrap gap-4 items-center">
            <span className="text-gray-700 font-medium">فیلتر بر اساس شهر:</span>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary-500"
            >
              <option value="">همه شهرها</option>
              {cities.map((city) => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Installers Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mockInstallers.map((installer) => (
            <div key={installer.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
                  <Briefcase className="w-8 h-8 text-primary-600" />
                </div>
                <div className="flex items-center gap-1 bg-yellow-50 px-2 py-1 rounded-lg">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-bold text-sm">{installer.rating}</span>
                  <span className="text-xs text-gray-500">({installer.total_reviews})</span>
                </div>
              </div>

              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {installer.company_name}
              </h3>
              
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {installer.bio}
              </p>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <MapPin className="w-4 h-4" />
                  <span>{installer.service_areas.join('، ')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Briefcase className="w-4 h-4" />
                  <span>{installer.experience_years} سال تجربه</span>
                </div>
              </div>

              <Link
                to={`/marketplace/installer/${installer.id}`}
                className="flex items-center justify-center gap-2 w-full bg-primary-50 hover:bg-primary-100 text-primary-700 font-medium py-3 rounded-lg transition-colors"
              >
                مشاهده پروفایل
                <ChevronLeft className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">نصاب هستید؟</h2>
          <p className="text-primary-100 mb-6">
            به جمع نصاب‌های حرفه‌ای ما بپیوندید و درآمد خود را افزایش دهید
          </p>
          <Link
            to="/register-installer"
            className="inline-block bg-white text-primary-700 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors"
          >
            ثبت‌نام نصاب
          </Link>
        </div>
      </div>
    </div>
  );
};